export const OPS_EMAIL_CHANNELS = ['store', 'licenses'] as const;
export type OpsEmailChannel = (typeof OPS_EMAIL_CHANNELS)[number];

export type OpsEmailRecipient = {
  email: string;
  store: boolean;
  licenses: boolean;
};

export const OPS_EMAIL_RECIPIENTS_KEY = 'ops_email_recipients';

export const OPS_EMAIL_CHANNEL_LABELS: Record<OpsEmailChannel, string> = {
  store: 'Tienda',
  licenses: 'Licencias',
};

export const DEFAULT_OPS_EMAIL_RECIPIENTS: OpsEmailRecipient[] = [
  { email: 'pedidos@aipeces.com', store: true, licenses: true },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isOpsEmailValid(raw: string): boolean {
  return EMAIL_RE.test(raw.trim().toLowerCase());
}

function parseChannelFlag(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return fallback;
}

export function normalizeOpsEmailRecipients(raw: unknown): {
  recipients: OpsEmailRecipient[];
  invalid: string[];
} {
  const invalid: string[] = [];
  const seen = new Set<string>();
  const recipients: OpsEmailRecipient[] = [];
  if (!Array.isArray(raw)) {
    return { recipients: [], invalid: ['lista'] };
  }
  for (const item of raw) {
    if (typeof item === 'string') {
      const email = item.trim().toLowerCase();
      if (!email) continue;
      if (!isOpsEmailValid(email)) {
        invalid.push(item);
        continue;
      }
      if (seen.has(email)) continue;
      seen.add(email);
      recipients.push({ email, store: true, licenses: true });
    } else if (item && typeof item === 'object') {
      const row = item as Record<string, unknown>;
      const email = String(row.email ?? '').trim().toLowerCase();
      if (!email) continue;
      if (!isOpsEmailValid(email)) {
        invalid.push(String(row.email ?? ''));
        continue;
      }
      if (seen.has(email)) continue;
      seen.add(email);
      recipients.push({
        email,
        store: parseChannelFlag(row.store, false),
        licenses: parseChannelFlag(row.licenses, false),
      });
    } else {
      invalid.push(String(item));
    }
    if (recipients.length >= 20) break;
  }
  return { recipients, invalid };
}

export function resolveOpsEmailRecipientsFromApi(
  raw: unknown
): { recipients: OpsEmailRecipient[]; fromApi: boolean } {
  if (raw == null) {
    return {
      recipients: DEFAULT_OPS_EMAIL_RECIPIENTS.map((item) => ({ ...item })),
      fromApi: false,
    };
  }
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return {
        recipients: DEFAULT_OPS_EMAIL_RECIPIENTS.map((item) => ({ ...item })),
        fromApi: true,
      };
    }
  }
  const { recipients } = normalizeOpsEmailRecipients(value);
  if (recipients.length === 0) {
    return {
      recipients: DEFAULT_OPS_EMAIL_RECIPIENTS.map((item) => ({ ...item })),
      fromApi: true,
    };
  }
  return { recipients, fromApi: true };
}

export function emptyOpsEmailRecipient(email: string): OpsEmailRecipient {
  return { email, store: true, licenses: true };
}
